import { useEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppWrapper, Spinner } from "components";
import styles from "./VerifyEmail.module.scss";
import checkmarkImg from "../../assets/images/green-checkmark.png";
import messages from "./messages";

const statuses = {
    loading: "loading",
    success: "success",
    error: "error",
};

const VerifyEmail = ({ intl }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const [status, setStatus] = useState(statuses.loading);
    const [message, setMessage] = useState("");
    const [countdown, setCountdown] = useState(5);

    const verificationStarted = useRef(false);

    useEffect(() => {
        if (verificationStarted.current) {
            return;
        }

        verificationStarted.current = true;

        if (!token) {
            setStatus(statuses.error);
            setMessage(
                intl.formatMessage(messages.invalidVerificationLink)
            );
            return;
        }

        const verifyEmail = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BASE_URL}users/verify-email/?token=${encodeURIComponent(token)}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(
                        intl.formatMessage(
                            messages.invalidVerificationLink
                        )
                    );
                }

                setStatus(statuses.success);
                setMessage(
                    data?.detail ||
                        intl.formatMessage(
                            messages.emailVerifiedMessage
                        )
                );
            } catch (error) {
                setStatus(statuses.error);
                setMessage(
                    intl.formatMessage(messages.unableToVerifyEmail)
                );
            }
        };

        verifyEmail();
    }, [token, intl]);

    useEffect(() => {
        if (status !== statuses.success) {
            return;
        }

        if (countdown === 0) {
            navigate("/");
            return;
        }

        const timer = setTimeout(() => {
            setCountdown((previous) => previous - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [status, countdown, navigate]);

    return (
        <AppWrapper>
            <div className={styles.page}>
                <div className={styles.card}>
                    {status === statuses.loading && (
                        <div className={styles.content}>
                            <Spinner show />

                            <h2 className={styles.title}>
                                {intl.formatMessage(
                                    messages.verifyingEmailTitle
                                )}
                            </h2>

                            <p className={styles.message}>
                                {intl.formatMessage(
                                    messages.verifyingEmailMessage
                                )}
                            </p>
                        </div>
                    )}

                    {status === statuses.success && (
                        <div className={styles.content}>
                            <div
                                className={`${styles.icon} ${styles.success}`}
                            >
                                <img
                                    src={checkmarkImg}
                                    alt=""
                                />
                            </div>

                            <h2 className={styles.title}>
                                {intl.formatMessage(
                                    messages.emailVerifiedTitle
                                )}
                            </h2>

                            <p className={styles.message}>
                                {message}
                            </p>

                            <p className={styles.redirectMessage}>
                                {intl.formatMessage(
                                    messages.redirectMessage,
                                    {
                                        countdown,
                                        red: (chunks) => (
                                            <span>{chunks}</span>
                                        ),
                                    }
                                )}
                            </p>
                        </div>
                    )}

                    {status === statuses.error && (
                        <div className={styles.content}>
                            <div
                                className={`${styles.icon} ${styles.error}`}
                            >
                                !
                            </div>

                            <h2 className={styles.title}>
                                {intl.formatMessage(
                                    messages.verificationFailedTitle
                                )}
                            </h2>

                            <p className={styles.message}>
                                {message}
                            </p>

                            <button
                                type="button"
                                className={styles.homeButton}
                                onClick={() => navigate("/")}
                            >
                                {intl.formatMessage(messages.goToHome)}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </AppWrapper>
    );
};

export default injectIntl(VerifyEmail);